import Forecast from "../charts/Forecast";
import CapMod from "../charts/CapMod";
import Returnify from "../charts/Returnify";
import { FaChalkboardTeacher, FaPaintBrush } from "react-icons/fa";
import {
  FaEarthAmericas,
  FaPeopleGroup,
  FaMapLocationDot,
  FaFileInvoiceDollar,
  FaRegChartBar,
} from "react-icons/fa6";
import { IoBarChart } from "react-icons/io5";
import { MdCurrencyExchange } from "react-icons/md";
import { GiPriceTag, GiStairsGoal } from "react-icons/gi";
import { BsPersonBoundingBox, BsStars } from "react-icons/bs";
import { PiDetectiveFill } from "react-icons/pi";
import { GrDirections, GrOptimize } from "react-icons/gr";
import { ImLocation2 } from "react-icons/im";
import { RiShieldUserFill } from "react-icons/ri";
import {
  FiGlobe,
  FiPieChart,
  FiTarget,
  FiUsers,
  FiFlag,
  FiTrendingUp,
  FiMap,
  FiCompass,
  FiBriefcase,
  FiShield,
  FiUserCheck,
  FiAward,
  FiLayers,
  FiCheckCircle,
} from "react-icons/fi";

// Images and video imports retained from the existing solutions pages.
const cartalyze =
  "https://spinnakerwebsiteassets.s3.amazonaws.com/spinnakeranalytics.com/videos/cartayze-chart.mp4";
const cartalyzePoster =
  "https://spinnakerwebsiteassets.s3.amazonaws.com/spinnakeranalytics.com/videos/posters/cartayze-chart.webp";

// Source of truth for current product and module names/descriptions.
// This data is aligned with product_catalog.json. Legacy modules are merged below.
const catalogProducts = [
  {
    "solution": "Sales Prophet™",
    "solution_slug": "/sales-prophet",
    "tagline": "Size your market and find where to grow sales.",
    "solution_description": "Accurately estimate market size, identify client-specific sales opportunities, and strategically allocate sales goals across regions, products, and customer segments.",
    "modules": [
      {
        "title": "Market Intelligence",
        "slug": "/market-intelligence",
        "description": "Analyze premiums, policies, agents, wallet share, and demographics by geography. Filter by year, product, metric, or state, then use heatmaps and ZIP-code tables to compare company performance with the market and identify high-potential areas.",
        "components": []
      },
      {
        "title": "Sales Opportunity",
        "slug": "/sales-opportunity",
        "description": "Rank sales and recruiting opportunities by state, territory, or ZIP code. Explore company sales, incremental and total opportunity, and opportunity percentages in map or table views, with filters for year, product, territory, and state.",
        "components": []
      },
    ]
  },
  {
    "solution": "Lead Prioritizer™",
    "solution_slug": "/lead-prioritizer",
    "tagline": "Score applications so teams work the best cases first.",
    "solution_description": "Utilize pattern recognition algorithms to prioritize applications or work volumes, maximizing yield rates, optimizing workflow, and reducing unit costs.",
    "modules": [
      {
        "title": "Work Inventory",
        "slug": "/work-inventory",
        "description": "Review and filter application-level inventory by applicant details, product, status, broker, underwriter, risk assessment, lead score, and priority. Track open and completed cases, highlight high-priority work, and identify workflow bottlenecks.",
        "components": []
      },
      {
        "title": "Score Calculator",
        "slug": "/score-calculator",
        "description": "Generate a case score from applicant and application attributes such as age, health and lifestyle details, product, broker, income, and face amount. View the priority category and factor contributions, adjust inputs, and export scoring results.",
        "components": []
      },
      {
        "title": "Attribute Analysis",
        "slug": "/attribute-analysis",
        "description": "Analyze lead scores and conversion across brokers, states, age and income groups, products, and face amounts. Review case counts, sold cases, closing ratios, premium, and forecast premium, with filters for application type and underwriter.",
        "components": []
      },
      {
        "title": "Priority Bin Dashboard",
        "slug": "/priority-bin-dashboard",
        "description": "Segment applications into High, Normal, Low, and DTQ priority bins. Compare case counts, sold rates, total and expected premiums, and average scores, with filters for product, application type, underwriter, broker, state, and income.",
        "components": []
      },
      {
        "title": "Score Distribution",
        "slug": "/score-distribution",
        "description": "Explore case-score distributions and their relationship to sales conversion. Compare sold and unsold cases, closing ratios, and priority groups, then filter by product, application type, underwriter, age, income, or state to identify effective score ranges and outliers.",
        "components": []
      }
    ]
  },
  {
    "solution": "UW Engine",
    "solution_slug": "/uw-engine",
    "tagline": "Automated underwriting risk ratings for term life.",
    "solution_description": "Efficiently assign risk ratings to prospective life customers purchasing transactional term products by leveraging minimally invasive customer and external data.",
    "modules": [
      {
        "title": "Engine",
        "slug": "/engine",
        "description": "Calculate real-time underwriting risk recommendations from applicant attributes such as demographics, lifestyle, family history, driving record, and blood pressure. View the resulting risk class and factor contributions, adjust inputs, and export results.",
        "components": []
      },
      {
        "title": "Underwriting Engine Framework",
        "slug": "/underwriting-engine-framework",
        "description": "Understand how applicant demographics, lifestyle, financial details, product information, health history, and external records feed the underwriting model. Review model outputs, predicted risk classes, and comparisons between predicted and actual classifications.",
        "components": []
      },
      {
        "title": "Performance Metrics",
        "slug": "/performance-metrics",
        "description": "Evaluate underwriting model accuracy using training, testing, and cross-validation results. Review preferred-class probability ranges, false positives, false negatives, and performance across different model variable sets to support model validation and refinement.",
        "components": []
      },
      {
        "title": "Submission Ingestion",
        "slug": "/submission-ingestion",
        "description": "Extract key submission details from inbound emails and attachments, including dates, industry, currency, revenue, region, broker, underwriter, and insured. Review and edit parsed fields, while automated replies identify missing information or confirm receipt.",
        "components": [
          {
            "title": "UW Manager",
            "slug": "/uw-manager",
            "description": "Configure extracted submission fields, set their priority, and define parsing guidance. Contextual rules help infer missing details and improve data completeness and consistency."
          },
          {
            "title": "UW Inbox",
            "slug": "/uw-inbox",
            "description": "Review parsed submissions in a sortable table with sender, subject, extracted fields, and attachment context. Edit submission details and move cases into the underwriting workflow."
          }
        ]
      }
    ]
  },
  {
    "solution": "Renewals Optimizer",
    "solution_slug": "/renewals-optimizer",
    "tagline": "Choose renewal actions that protect profit and retention.",
    "solution_description": "Enhance profitability by applying targeted renewal actions informed by historical sales and claims experience data.",
    "modules": [
      {
        "title": "Renewals Estimator",
        "slug": "/renewals-estimator",
        "description": "Estimate and adjust renewal rates using RTM methodology for Employee Benefits or RTF for Commercial Lines–P&C. Compare historical premiums, losses, and benchmark rates, review account profiles, and model recommended rate changes and premium outcomes.",
        "components": []
      },
      {
        "title": "Target List",
        "slug": "/target-list",
        "description": "Identify sponsors or policyholders with persistently unprofitable renewal histories. Review account, broker, territory, employee or company size, and product details; set inclusion thresholds and compare target accounts with the full portfolio.",
        "components": []
      },
      {
        "title": "Renewals Dashboard",
        "slug": "/renewals-dashboard",
        "description": "Compare renewal performance with RTM benchmarks for Employee Benefits or RTF benchmarks for Commercial Lines–P&C. Review policies, premiums, lapse rates, rate changes, claims, and loss ratios by market segment, with distribution charts to identify accounts needing action.",
        "components": []
      },
      {
        "title": "Pricing & Economics",
        "slug": "/pricing-and-economics",
        "description": "Analyze price sensitivity against two-year sales to assess renewal-rate changes for Commercial Lines–P&C. Segment accounts by demand and sensitivity, then use the results to guide pricing actions while balancing revenue, margin, and competitiveness.",
        "components": []
      }
    ]
  },
  {
    "solution": "Commission Intelligence",
    "solution_slug": "/commission-intelligence",
    "tagline": "Manage and optimize broker commission contracts.",
    "solution_description": "Optimize commission structures by analyzing contracts and scenarios for both retail and wholesale brokerage.",
    "modules": [
      {
        "title": "Contract Ingestion",
        "slug": "/contract-ingestion",
        "description": "Extract structured commission terms from scanned or electronic contracts for Property & Casualty and Employee Benefits. Review carrier, program, bonus, threshold, compensation, payment, and effective-date details before using the data for analysis.",
        "components": []
      },
      {
        "title": "Contract Summary",
        "slug": "/contract-summary",
        "description": "Search, filter, and export structured commission contract records for Property & Casualty and Employee Benefits. Review carrier, program, bonus, threshold, compensation, product, payment, effective-date, and source-document details.",
        "components": []
      },
      {
        "title": "Contract Comparison",
        "slug": "/contract-comparison",
        "description": "Compare two commission contracts side by side across schedules, bonus plans, thresholds, eligibility, rates, caps, payment timing, and exclusions. Supports Property & Casualty and Employee Benefits, with key differences highlighted for review.",
        "components": []
      },
      {
        "title": "Commission Tool",
        "slug": "/commission-tool-agency-level",
        "description": "Model and optimize contingent commissions across carriers for Property & Casualty and Employee Benefits. Set loss-ratio and premium thresholds, compare current and reallocated commission mixes, and estimate commission gains and enterprise-value impact.",
        "components": []
      },
      {
        "title": "Commission Forecasting",
        "slug": "/commission-forecasting",
        "description": "Forecast Property & Casualty commission income by month, quarter, and year. Compare Actuals, Budget, and 4Qast projections, review variances, and filter results by business segment, region, and metric subtype.",
        "components": []
      }
    ]
  },
  {
    "solution": "Cartalyze™",
    "solution_slug": "/cartalyze",
    "tagline": "Cross-sell and product recommendations by customer segment.",
    "solution_description": "Receive automatic product recommendations tailored to various demographics and life stages. Design targeted pricing actions and promotional campaigns to enhance profitability and elevate marketing strategies.",
    "modules": [
      {
        "title": "Product Recommendation",
        "slug": "/product-recommendation",
        "description": "Automatically suggest products and solutions tailored to individual life stages and priorities, aligned with demographic attributes. (Details TBD)",
        "components": []
      },
      {
        "title": "Cross-sell Engine",
        "slug": "/cross-sell-engine",
        "description": "Recommend cross-sell products to Employee Benefits customers based on products they already own. Rank recommendations by cross-sell score and review support, confidence, lift, and product ownership patterns using customer and portfolio filters.",
        "components": []
      },
      {
        "title": "Cross-sell Dashboard",
        "slug": "/cross-sell-dashboard",
        "description": "In-force and cross-sell monitoring dashboard for Employee Benefits. Shows total in-force premium, in-force policies, in-force products and products per policy by state and by product category, cross-sell sales results, and territory performance. Filters include territory, state and broker.",
        "components": []
      },
      {
        "title": "Pricing Optimizer",
        "slug": "/pricing-optimizer",
        "description": "Analyze product demand and price sensitivity using a sales-versus-sensitivity matrix. Group products into actionable segments and use the results to guide price increases, reduced discounting, or promotional actions.",
        "components": []
      },
      {
        "title": "Customer Profiler",
        "slug": "/customer-profiler",
        "description": "Group Individual Life prospects into behavioral and demographic segments and estimate policy placement likelihood. Use attributes such as age, income, location, broker, and risk indicators to identify high-converting segments and guide recommendations.",
        "components": []
      }
    ]
  },
  {
    "solution": "4Qast™",
    "solution_slug": "/4qast",
    "tagline": "Forecast sales and track variance against plan.",
    "solution_description": "Analyze historical data and apply statistical techniques to predict future results accurately. Identify key trends and variance drivers for proactive decision-making.",
    "modules": [
      {
        "title": "Leadership Dashboard",
        "slug": "/leadership-dashboard",
        "description": "Monitor revenue, expenses, and EBITDA across periods, regions, products, and profit centers. Compare Actuals, Budget, 4Qast forecasts, and prior-year results, with breakdowns of core and contingent revenue, expense categories, and profitability metrics.",
        "components": []
      },
      {
        "title": "4Qast Dashboard",
        "slug": "/4qast-dashboard",
        "description": "Track monthly sales and revenue against forecasts, goals, and prior-year results. Review YTD and full-year totals, variance analysis, and forecast breakdowns, with filters for industries, regions, territories, products, segments, and size bins.",
        "components": []
      },
      {
        "title": "4Qast Drill Down",
        "slug": "/4qast-drill-down",
        "description": "Use a sales waterfall to explain forecast-versus-goal variance by product, segment, size, region, or territory. Each category shows its positive or negative revenue contribution, helping isolate the drivers of forecast changes.",
        "components": []
      }
    ]
  },
  {
    "solution": "CapMod™",
    "solution_slug": "/capmod",
    "tagline": "Model staffing and capacity by function.",
    "solution_description": "Optimally align skill sets and determine staffing requirements by function, considering factors such as work complexity, seasonality, and cross-training.",
    "modules": [
      {
        "title": "Claims Forecasting",
        "slug": "/claims-forecasting",
        "description": "Forecast of new claims by line of business. (Details TBD)",
        "components": []
      },
      {
        "title": "Claims Staffing Model",
        "slug": "/claims-staffing",
        "description": "Forecast future staffing needs based on historical productivity, staffing patterns, work complexity, volume forecasting, and seasonality. (Details TBD)",
        "components": [
          {
            "title": "Claims Staffing Model",
            "slug": "/claims-staffing-model",
            "description": "Claims staffing forecasting model dashboard. (Details TBD)"
          },
          {
            "title": "Productivity Charts",
            "slug": "/productivity-charts",
            "description": "Compare unit or product-level headcount against productivity standards. The chart groups staff into above-standard and below-standard bands, helping leaders identify capacity gaps, overperformance, coaching needs, and cross-training opportunities."
          },
          {
            "title": "Productivity Comparison",
            "slug": "/productivity-comparison",
            "description": "Compare individual productivity against standards across the current and prior year. A quadrant view highlights consistent high performers, improved or declining performance, and persistent underperformance to help identify latent capacity."
          },
          {
            "title": "Latent Capacity",
            "slug": "/latent-capacity",
            "description": "Estimate unused capacity by comparing staff productivity with defined standards. A ranked chart groups employees by capacity bands and helps identify whether stronger performers can absorb work from persistently lower-performing staff."
          },
          {
            "title": "Payband Productivity",
            "slug": "/payband-productivity",
            "description": "Compare productivity and work complexity across pay bands and job levels. Current- and prior-period results show whether higher-paid staff meet expected standards and help identify cost, capacity, and workload-rebalancing opportunities."
          }
        ]
      },
      {
        "title": "Operations Transformation Planning",
        "slug": "/operations-transformation-planning",
        "description": "Build staffing scenarios for automation, offshoring, and projected growth rates using stochastic and business plan forecasts with cost-benefit analysis. (Details TBD)",
        "components": []
      },
      {
        "title": "NBUW Staffing Model",
        "slug": "/nbuw-staffing-model",
        "description": "Analyze historical work volumes, staffing levels, and productivity to forecast future volumes and staffing needs. (Details TBD)",
        "components": []
      },
      {
        "title": "IT Staffing Model",
        "slug": "/it-staffing-model",
        "description": "Map skill sets to staff and visualize utilization by project and skill set; optimize utilization by resequencing projects. (Details TBD)",
        "components": []
      },
      {
        "title": "Productivity Assessment",
        "slug": "/productivity-assessment",
        "description": "Utilize cohort analytics to identify productivity drivers and establish targeted productivity levels. (Details TBD)",
        "components": [
          {
            "title": "UW Performance Analysis",
            "slug": "/uw-performance-analysis",
            "description": "Underwriting performance and cohort productivity analysis dashboard. (Details TBD)"
          },
          {
            "title": "Competitive Landscape",
            "slug": "/competitive-landscape",
            "description": "Compare submission and quote outcomes across carriers to identify retention and competitive wins or losses. A carrier matrix and supporting counts and quote ratios reveal where business is moving and where competitive pressure is strongest."
          }
        ]
      }
    ]
  },
  {
    "solution": "Returnify",
    "solution_slug": "/returnify",
    "tagline": "Predict product-return propensity for e-commerce.",
    "solution_description": "Predict product return propensity using historical data, uncover omni-channel opportunities, and identify future store locations with sales potential.",
    "modules": [
      {
        "title": "Returns Propensity Score Calculator",
        "slug": "/returns-propensity-score-calculator",
        "description": "Develop real-time transaction return probabilities based on customer behavior and transaction attributes. Scores individual customers or products on their likelihood to generate a return, using propensity modelling over e-commerce and consumer goods data.",
        "components": []
      },
      {
        "title": "Returns Propensity Dashboard",
        "slug": "/returns-propensity-dashboard",
        "description": "Visualize customer behavior across different channels to identify patterns in returns. Dashboard view of returns propensity scores across the customer or product catalogue. Surfaces the highest-risk return segments for targeted intervention.",
        "components": []
      }
    ]
  },
  {
    "solution": "Scenario Planner",
    "solution_slug": "/scenario-planner",
    "tagline": "Test the financial and staffing impact of new initiatives.",
    "solution_description": "Assess and analyze scenarios pertaining to new initiatives to determine impact on financials, staffing and unit costs.",
    "modules": [
      {
        "title": "Unit Cost",
        "slug": "/unit-cost",
        "description": "Financial & Operational cost planning tool to project year-over-year volume, sales and unit cost. Models the impact of business initiatives on unit cost, expenses, headcount and premium volume. A lever-driven financial planning tool that projects outcomes across multiple scenarios.",
        "components": []
      },
      {
        "title": "Zero Based Budget",
        "slug": "/zero-based-budget",
        "description": "Establish targets and mechanisms for arriving at the end-state expense structure for ongoing business/entity. Supports zero-based budgeting by modelling expenses from first principles rather than incrementally from last year's budget.",
        "components": []
      }
    ]
  },
  {
    "solution": "Lapse Predictor",
    "solution_slug": "/lapse-predictor",
    "tagline": "Spot cases at risk of lapsing before they come up for renewal.",
    "solution_description": "Assess renewal lapse risk using case attributes. The model assigns each policy a Low, Normal, or High risk score, helping underwriters and account managers identify accounts that may need proactive retention actions.",
    "modules": [
      {
        "title": "Policy List",
        "slug": "/policy-list",
        "description": "Review Individual Life policies with model-generated lapse scores and suggested retention actions. Each record includes policy, product, territory, distribution channel, effective month, income segment, face amount, premium, and selected applicant attributes.",
        "components": []
      },
      {
        "title": "Lapse Prediction Tool",
        "slug": "/lapse-predictor-tool",
        "description": "Score an individual case for renewal lapse risk and classify it as Low, Normal, or High. The calculation considers product, coverage, territory, timing, market segment, sales representative, eligible lives, premium, loss ratio, rate change, and industry.",
        "components": []
      }
    ]
  },
  {
    "solution": "DECISIS",
    "solution_slug": "/decisis",
    "tagline": "Ask questions across all your systems and get sourced, auditable answers.",
    "solution_description": "Connect business systems so users can ask questions in plain language and receive sourced, auditable answers with recommendations and impact. DECISIS also supports reporting and action tracking across connected applications.",
    "modules": [
      {
        "title": "ERP for AI",
        "slug": "/erp-for-ai",
        "description": "Plan and track AI transformation programmes across insurance and financial services. Combine historical financials, headcount, and volumes with initiative scenarios, FTE-efficiency modelling, five-year P&L and cost-benefit analysis, capacity diagnostics, and quarterly plan-versus-actual reporting.",
        "components": []
      }
    ]
  },
  {
    "solution": "Data Enrichment",
    "solution_slug": "/data-enrichment",
    "tagline": "Enrich your data with Form 5500 and carrier data.",
    "solution_description": "Enrich your data with external data sources, including Form 5500 filings and carrier analysis, to add context across industries, with a focus on Employee Benefits.",
    "modules": [
      {
        "title": "Form 5500",
        "slug": "/form-5500",
        "description": "Provides access to Form 5500 filing data for Employee Benefits market intelligence. Used to enrich prospect and client data with external employer benefit plan information.",
        "components": []
      }
    ]
  }
];

const productPresentation = {
  "sales-prophet": {
    icon: <FaEarthAmericas />,
    listItems: [
      "Estimate granular market size",
      "Assess wallet share",
      "Design sales territory",
    ],
    isIframe: true,
    iframe: (
      <iframe
        className="h-44 sm:h-60 w-full lg:h-80 iframe"
        src="https://www.youtube.com/embed/GiunKXmcwIE"
        title="Sales Prophet product overview"
        referrerPolicy="strict-origin-when-cross-origin"
        loading="lazy"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        allowFullScreen
      ></iframe>
    ),
  },
  "lead-prioritizer": {
    icon: <FaChalkboardTeacher />,
    listItems: [
      "Identify high-value prospects",
      "Develop customer segmentation",
      "Improve marketing ROI",
    ],
    isIframe: true,
    iframe: (
      <iframe
        className="h-44 sm:h-60 w-full lg:h-80 iframe"
        src="https://www.youtube.com/embed/hOwWm-EUeIQ?si=M0nA-EuEU0x6REWs"
        title="Lead Prioritizer product overview"
        referrerPolicy="strict-origin-when-cross-origin"
        loading="lazy"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        allowFullScreen
      ></iframe>
    ),
  },
  "uw-engine": {
    icon: <RiShieldUserFill />,
  },
  "renewals-optimizer": {
    icon: <MdCurrencyExchange />,
  },
  "commission-intelligence": {
    icon: <FaFileInvoiceDollar />,
  },
  cartalyze: {
    icon: <GiPriceTag />,
    listItems: [
      "Develop product recommendation engine",
      "Optimize pricing strategy",
    ],
    chart: (
      <video
        src={cartalyze}
        poster={cartalyzePoster}
        playsInline
        autoPlay
        muted
        className="video bg-white w-full"
      ></video>
    ),
  },
  "4qast": {
    icon: <IoBarChart />,
    listItems: [
      "Forecast sales at granular level",
      "Quantify variance and drivers",
      "Develop scenario planning",
    ],
    chart: <Forecast />,
  },
  capmod: {
    icon: <FaPeopleGroup />,
    listItems: [
      "Determine staffing requirements",
      "Identify work drivers",
      "Incorporate seasonality, complexity and cross-training",
    ],
    chart: <CapMod />,
  },
  returnify: {
    icon: <MdCurrencyExchange />,
    listItems: [
      "Predict product return propensity",
      "Develop omni channel opportunities",
      "Identify future store location and sales potential",
    ],
    chart: <Returnify />,
  },
  "scenario-planner": {
    icon: <IoBarChart />,
  },
  "lapse-predictor": {
    icon: <FaRegChartBar />,
  },
  decisis: {
    icon: <BsStars />,
  },
  "data-enrichment": {
    icon: <FaEarthAmericas />,
  },
};

// Modules present in the old solutions file but not in the current catalog.
// These are intentionally retained. Where a title also exists in the catalog,
// the catalog version is used instead so its current description takes priority.
const legacyModules = {
  "sales-prophet": [
    {
      title: "Agent Productivity",
      slug: "agent-productivity",
      icon: <GiStairsGoal />,
      description: (
        <span className="lg:mt-7 inline-block">
          Use this module to analyze Ameritas agents down to the individual agent level to inform strategies to improve their productivity. This includes tracking of agent KPIs such as ratio of producing agents to appointed agents, sales per producing agent, and new agents recruited
        </span>
      ),
    },
    {
      title: "Firm Scorecard",
      slug: "firm-scorecard",
      icon: <FaMapLocationDot />,
      description: (
        <span className="lg:mt-7 inline-block">
          Use this module to create detailed scorecards down to the individual firm level for proactive discussions with distribution partners. These scorecards summarize performance across sales, submissions, inforce, placement/lapse rates, customer product/age/FA mix, and more
        </span>
      ),
    },
    {
      title: "Goal Setter",
      slug: "goal-setter",
      icon: <FaMapLocationDot />,
      description: (
        <span className="lg:mt-7 inline-block">
          Use this module to automatically set and assign new sales goals by Ameritas regions, products, and channels using combination of market data, Ameritas performance, and new sales opportunity, with management input for targeted reallocation of goals
        </span>
      ),
    },
    {
      title: "Territory Designer",
      slug: "territory-designer",
      icon: <FaMapLocationDot />,
      description: (
        <span className="lg:mt-7 inline-block">
          Use this module to visualize, design, and balance scenarios and options for Ameritas geographic regions by channel, combining data from each Sales Prophet™ module for a comprehensive view of market data, Ameritas performance, and new sales opportunity
        </span>
      ),
    },
  ],
  "lead-prioritizer": [
    {
      title: "App Prioritizer",
      slug: "app-prioritizer",
      icon: <FaChalkboardTeacher />,
      description:
        "Prioritize work volumes using pattern recognition algorithms to maximize yield rates, optimize workflow, and reduce unit costs",
    },
    {
      title: "Customer Profiler",
      slug: "customer-profiler",
      icon: <BsPersonBoundingBox />,
      description:
        "Analyze customer behavior patterns, identify target segments, develop product recommendations, and pin-point retention drivers",
    },
    {
      title: "Fraud Detection",
      slug: "fraud-detection",
      icon: <PiDetectiveFill />,
      description:
        "Analyze historical data to identify the normal behavior of each customer, and compare it with external data to spot any anomalies. Set up automatic alerts that indicate the probability of fraud based on the detected activities.",
    },
  ],
  "4qast": [
    {
      title: "Sales Forecast",
      slug: "sales-forecast",
      icon: <IoBarChart />,
      description:
        "Accurately and dynamically forecasts sales and volumes by business segment, product line, geography, channel, and other key attributes",
    },
    {
      title: "Unit Cost Forecasting",
      slug: "unit-cost-forecasting",
      icon: <FaFileInvoiceDollar />,
      description:
        "Dynamically forecasts cost per contract from sales to service based on product, fixed, variable cost structure, currency exchange rate, etc.",
    },
  ],
  capmod: [
    {
      title: "Staffing Model",
      slug: "staffing-model",
      icon: <FaPeopleGroup />,
      description:
        "Optimally align skillsets and determine staffing requirements by function while adjusting for complexity, seasonality, cross-training",
    },
    {
      title: "Resource Navigator",
      slug: "resource-navigator",
      icon: <GrDirections />,
      description:
        "Analyze the actual cost to serve along the complete lifecycle of the product/commodity to identify cost and resource drivers at each stage of the product lifecycle",
    },
  ],
  returnify: [
    {
      title: "Return Propensity Score",
      slug: "return-propensity-score",
      icon: <MdCurrencyExchange />,
      description: (
        <span className="lg:mt-7 2xl:mt-0 inline-block">
          Develop real-time transaction return probability based on customer behavior and transaction attributes
        </span>
      ),
    },
    {
      title: "Store Site Identification",
      slug: "store-site-identification",
      icon: <ImLocation2 />,
      description: (
        <span className="lg:mt-7 2xl:mt-0 inline-block">
          Identify new store locations and formats at zip-code level based on physical and digital customer behavior
        </span>
      ),
    },
    {
      title: "Loyalty Program Analytics",
      slug: "loyalty-program-analytics",
      icon: <BsStars />,
      description:
        "Analyze buyer behavior patterns, identify target segments, develop product recommendations, and pin-point retention drivers",
    },
  ],
  cartalyze: [
    {
      title: "Optimize Pricing Strategy",
      slug: "optimize-pricing-strategy",
      icon: <GrOptimize />,
      description:
        "Design targeted pricing actions and impactful promotional campaigns to increase profitiblity",
    },
  ],
};

const normalizeTitle = (title = "") => title.trim().toLowerCase();

// Reused by position within each product, so every module in a product gets
// a different icon. The current largest product has 14 modules including
// legacy-only entries, so this set avoids repeats within any current product.
const moduleIcons = [
  <FiGlobe />,
  <FiPieChart />,
  <FiTarget />,
  <FiUsers />,
  <FiFlag />,
  <FiTrendingUp />,
  <FiMap />,
  <FiCompass />,
  <FiBriefcase />,
  <FiShield />,
  <FiUserCheck />,
  <FiAward />,
  <FiLayers />,
  <FiCheckCircle />,
];

const createCatalogModules = (product) => {
  return (product.modules || []).map((module, index) => {
    const slug = (module.slug || "").replace(/^[/]/, "");
    return {
      id: index + 1,
      title: module.title,
      slug,
      description: module.description,
      url: `/contact?product=${slug}`,
      components: (module.components || []).map((component) => ({
        title: component.title,
        slug: (component.slug || "").replace(/^[/]/, ""),
        description: component.description,
      })),
    };
  });
};

const createSolutionContent = (product) => {
  const productSlug = (product.solution_slug || "").replace(/^[/]/, "");
  const presentation = productPresentation[productSlug] || {};
  const catalogContent = createCatalogModules(product);
  const catalogTitles = new Set(
    catalogContent.map((module) => normalizeTitle(module.title))
  );
  const retainedLegacyContent = (legacyModules[productSlug] || []).filter(
    (module) => !catalogTitles.has(normalizeTitle(module.title))
  );
  const content = [...catalogContent, ...retainedLegacyContent];

  return {
    id: catalogProducts.findIndex(
      (item) => item.solution_slug === product.solution_slug
    ) + 1,
    title: product.solution,
    tagline: product.tagline,
    listItems: presentation.listItems || [product.tagline],
    icon: presentation.icon,
    slug: productSlug,
    description: product.solution_description,
    isIframe: presentation.isIframe || false,
    ...(presentation.iframe ? { iframe: presentation.iframe } : {}),
    ...(presentation.chart ? { chart: presentation.chart } : {}),
    content: content.map((module, index) => ({
      ...module,
      id: index + 1,
      icon: moduleIcons[index % moduleIcons.length],
      url: module.url || `/contact?product=${module.slug}`,
    })),
  };
};

export const solutionContent = [
  ...catalogProducts.map(createSolutionContent),
  {
    id: catalogProducts.length + 1,
    title: "Custom Solutions",
    slug: "custom-solutions",
    icon: <FaPaintBrush />,
  },
];